import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ErrorBar,
  Legend,
} from "recharts";

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
  "#06b6d4", "#f97316", "#14b8a6", "#a855f7", "#e11d48", "#84cc16",
];

const ConditionMeansChart = ({ stats, conditions, scaleInfo, height = 200 }) => {
  if (!stats?.byCondition || Object.keys(stats.byCondition).length === 0) {
    return <p className="text-gray-500 text-sm">No data</p>;
  }

  const conditionIds = conditions.map((c) => c.conditionId);
  const conditionLabels = {};
  conditions.forEach((c) => {
    conditionLabels[c.conditionId] = c.label || c.conditionId;
  });

  // Single grouped bar: one bar per condition
  const chartData = [
    {
      name: "Mean",
      ...Object.fromEntries(
        conditionIds.map((cid) => [cid, stats.byCondition[cid]?.mean || 0])
      ),
      ...Object.fromEntries(
        conditionIds.map((cid) => [`${cid}_sd`, stats.byCondition[cid]?.sd || 0])
      ),
    },
  ];

  // Determine Y domain
  const minDomain = scaleInfo?.min ?? 0;
  const maxDomain = scaleInfo?.max ?? scaleInfo?.points ?? 7;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-xs">
        {payload.map((entry, i) => {
          const cid = entry.dataKey;
          const sd = entry.payload[`${cid}_sd`] || 0;
          const n = stats.byCondition[cid]?.n || 0;
          return (
            <div key={i} className="text-gray-200">
              <span style={{ color: entry.fill }}>{conditionLabels[cid] || cid}</span>
              {": "}M = {entry.value?.toFixed(2)}, SD = {sd.toFixed(2)}, n = {n}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis dataKey="name" tick={false} height={5} />
        <YAxis
          domain={[minDomain, maxDomain]}
          tick={{ fill: "#9ca3af", fontSize: 11 }}
          stroke="#4b5563"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          formatter={(value) => (
            <span className="text-gray-300 text-xs">{conditionLabels[value] || value}</span>
          )}
        />
        {conditionIds.map((cid, i) => (
          <Bar
            key={cid}
            dataKey={cid}
            fill={CONDITION_COLORS[i % CONDITION_COLORS.length]}
            name={cid}
            barSize={40}
          >
            <ErrorBar
              dataKey={`${cid}_sd`}
              width={6}
              stroke={CONDITION_COLORS[i % CONDITION_COLORS.length]}
              strokeWidth={1.5}
            />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ConditionMeansChart;
